// REACT IMPORTS
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./RFAUserFormLog.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import { CircularProgress, IconButton } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import TanStackTable from "../../Misc/Table/TanStackTable";
import RFAUserFormLogDetails from "./RFAUserFormLogDetails/RFAUserFormLogDetails";

// CONSTANTS
import {
	dateSort,
	defaultSort,
	notApplicable,
} from "../../Misc/Table/TanStackTableConstants";
import { convertToPST } from "../../../constants/dateTime";

// REACT COMPONENT
const RFAUserFormLog = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// CONTEXT
	const employeeID = useContext(UserContext);

	// LOGS STATE
	const [rfaFormLogs, setRFAFormLogs] = useState([]);

	const [rfaFormLogsColumns, setRFAFormLogsColumns] = useState([]);
	const [rfaFormLogsRows, setRFAFormLogsRows] = useState([]);
	const [dealNamesById, setDealNamesById] = useState({});

	const [detailsOpen, setDetailsOpen] = useState(false);
	const [recordToView, setRecordToView] = useState(null);

	// LOADING STATES
	const [isLoading, setIsLoading] = useState(false);

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "RFA User Form Log",
			employeeID: employeeID,
			notes: "Accessed the RFA User Form Log page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		fetchRFAFormLogs();
	}, []);

	useEffect(() => {
		createColumnData();
		createRowData();
	}, [rfaFormLogs, dealNamesById]);

	useEffect(() => {
		const dealIDs = [
			...new Set(
				rfaFormLogs.map((log) => log.hubspot_deal_id).filter(Boolean),
			),
		];
		const missingDealIDs = dealIDs.filter((id) => !dealNamesById[id]);

		if (missingDealIDs.length === 0) return;

		const fetchDealNames = async () => {
			const results = await Promise.allSettled(
				missingDealIDs.map((dealID) =>
					getAPICall(
						instance,
						accounts[0],
						"/api/hubspot/getDealFromDealID",
						{ dealID },
					),
				),
			);

			const updates = {};
			results.forEach((result, index) => {
				const dealID = missingDealIDs[index];
				if (result.status === "fulfilled") {
					updates[dealID] =
						result.value?.data?.properties?.dealname ||
						notApplicable;
				} else {
					updates[dealID] = notApplicable;
				}
			});

			setDealNamesById((prev) => ({ ...prev, ...updates }));
		};

		fetchDealNames();
	}, [rfaFormLogs, dealNamesById, instance, accounts]);

	// INPUT HANDLERS

	// API HELPER FUNCTIONS
	const fetchRFAFormLogs = async () => {
		setIsLoading(true);

		try {
			const response = await getAPICall(
				instance,
				accounts[0],
				"/api/logs/rfa",
			);
			setRFAFormLogs(response.data);
		} catch (error) {
			console.error(error);
		}

		setIsLoading(false);
	};

	const createColumnData = () => {
		setRFAFormLogsColumns([
			// submission details
			{
				accessorKey: "submitted_at",
				header: () => <span>Submitted At</span>,
				label: "Submitted At",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "date",
				filterFn: dateSort,
			},
			{
				accessorKey: "submitted_by_employee_name",
				header: () => <span>Submitted By</span>,
				label: "Submitted By",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "deal_name",
				header: () => <span>Deal Name</span>,
				label: "Deal Name",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			// error
			{
				accessorKey: "error_message",
				header: () => <span>Error Message</span>,
				label: "Error Message",
				cell: (props) => (
					<span
						style={{
							color: props.getValue() ? "red" : "black",
						}}
					>
						{props.getValue() || notApplicable}
					</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			// more details button
			{
				accessorKey: "moreDetails",
				header: "",
				label: "More Details",
				cell: ({ row }) => (
					<IconButton
						onClick={() => detailsClicked(row.original.record)}
						disableRipple={true}
						sx={{
							justifyContent: "flex-end",
						}}
					>
						<MoreHorizIcon fontSize="small" />
					</IconButton>
				),
				enableColumnFilter: false,
				enableSorting: false, // sorting is true by default
			},
		]);
	};

	const createRowData = () => {
		setRFAFormLogsRows(
			rfaFormLogs.map((log) => ({
				// submission details
				submitted_at: convertToPST(log.submitted_time),
				submitted_by_employee_name: log.submitted_by_employee_name,
				deal_name: log.hubspot_deal_id
					? dealNamesById[log.hubspot_deal_id] || "Loading..."
					: notApplicable,
				// error
				error_message: log.error_message,
				// more details button
				record: log,
			})),
		);
	};

	// HELPER FUNCTIONS
	const detailsClicked = (record) => {
		setRecordToView(record);
		setDetailsOpen(true);
	};

	// RENDER
	return (
		<div className="RFAUserFormLog">
			<div className="RFAUserFormLogHeader">
				<div className="RFAUserFormLogHeaderTitle">
					RFA Submission Logs
				</div>
			</div>
			{isLoading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!isLoading && (
				<div className="tableContainerDiv">
					{rfaFormLogs.length === 0 && <J2NoResultsFound />}
					{rfaFormLogs.length > 0 &&
						rfaFormLogsColumns.length > 0 &&
						rfaFormLogsRows.length > 0 && (
							<TanStackTable
								columnData={rfaFormLogsColumns}
								rowData={rfaFormLogsRows}
							/>
						)}
				</div>
			)}
			{detailsOpen && recordToView && (
				<RFAUserFormLogDetails
					open={detailsOpen}
					recordToView={recordToView}
					setOpen={setDetailsOpen}
				/>
			)}
		</div>
	);
};

export default RFAUserFormLog;
